// SingleStaff.js
// Single staff — everything held about ONE person, in one place.  (v2)
//
// Mirrors the "Single pharmacy" pattern: pick a pharmacy, pick a staff
// member, see their whole file. Selectors are pharmacy → staff, with a
// "Show inactive staff" toggle so leavers can still be looked up (their
// documents, SOP history and leave record outlive their employment and
// are routinely needed for references, disputes and inspection).
//
// v2 — every per-staff dataset the platform holds, behind tabs:
//   Documents    staff_disciplinary_documents
//   Safety       near_misses + patients_medication_incidents (attribution
//                only — no patient-identifying data reaches this page)
//   Attendance   staff_attendance_logs + staff_break_logs + overtime,
//                each clocking scored against the AGREED pattern in
//                staff_times (the same source the clock in/out panel uses)
//   SOPs         sop_assignments × sops
//   Training     staff_training_assignments (not live yet — panel says so)
//   Leave        leave_requests
//   RP sessions  rp_signin_logs (pharmacists)
//   Messages     fetched from the Communications module's own endpoint,
//                with a compose dialogue that posts to the SAME send
//                endpoint the Communications UI uses
//
// All but Messages arrive from POST /api/single-staff/profile in one
// round trip, over a rolling window (default 12 months) matching the
// appraisal evidence pack. Messages come from
// /api/headoffice/staff-messages/list so there is ONE message history on
// the platform rather than two that can disagree.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import StaffAppraisal from "./StaffAppraisal";
import { displayName, useMaskNames, MaskToggleButton } from "./nameMask";

const API = "https://api.pharmacegroup.co.uk";

function authHeaders() {
  const token = localStorage.getItem("token") || localStorage.getItem("authToken");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

const post = async (path, body) => {
  const r = await fetch(`${API}${path}`, {
    method: "POST", headers: authHeaders(), body: JSON.stringify(body)
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
  return d;
};

/* ---------------- Formatting ---------------- */

const fmtDMY = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—"
    : t.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (d) => {
  if (!d) return "";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "" : t.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};
const fmtDateTime = (d) => (d ? `${fmtDMY(d)} ${fmtTime(d)}`.trim() : "—");
const fmtMins = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60), r = m % 60;
  return h ? `${h}h ${String(r).padStart(2, "0")}m` : `${r}m`;
};
const pretty = (v) => {
  if (!v) return "—";
  const s = String(v).replace(/_/g, " ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/* ---------------- Styles ---------------- */

const selStyle = {
  padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6,
  fontSize: 14, background: "#fff", minWidth: 240, fontFamily: "inherit"
};
const card = { border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", overflow: "hidden" };
const th = {
  textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: 0.3, padding: "7px 10px",
  borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap"
};
const td = { fontSize: 13, padding: "7px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" };

const TEAL = { bg: "#f0fdfa", br: "#5eead4", fg: "#0f766e" };
const pill = (bg, br, fg) => ({
  display: "inline-block", background: bg, border: `1px solid ${br}`, color: fg,
  borderRadius: 999, padding: "2px 9px", fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap"
});
const P = {
  green:  pill("#dcfce7", "#86efac", "#166534"),
  amber:  pill("#fef3c7", "#fcd34d", "#92400e"),
  red:    pill("#fee2e2", "#fca5a5", "#991b1b"),
  blue:   pill("#e0f2fe", "#7dd3fc", "#075985"),
  grey:   pill("#f3f4f6", "#d1d5db", "#4b5563"),
  teal:   pill(TEAL.bg, TEAL.br, TEAL.fg),
  purple: pill("#ede9fe", "#c4b5fd", "#5b21b6")
};

const Empty = ({ children }) => (
  <div style={{ padding: 22, fontSize: 13, color: "#6b7280" }}>{children}</div>
);

const Table = ({ head, children }) => (
  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
      <tr>{head.map((h, i) => <th key={`${h}-${i}`} style={th}>{h}</th>)}</tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

const Scroll = ({ children }) => (
  <div style={{ maxHeight: 460, overflowY: "auto" }}>{children}</div>
);

/* Small figure tile used along the top of each tab. */
const Stat = ({ label, value, tone }) => (
  <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", minWidth: 108, background: "#fff" }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 800, marginTop: 2, color: tone || "#111827" }}>{value}</div>
  </div>
);
const StatRow = ({ children }) => (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>{children}</div>
);

const Note = ({ tone = P.grey, children }) => (
  <div style={{ ...tone, display: "block", padding: "8px 12px", borderRadius: 6, fontSize: 12.5,
                fontWeight: 500, marginBottom: 12, whiteSpace: "normal" }}>
    {children}
  </div>
);

/* ---------------- Tab panels ---------------- */

function DocumentsTab({ rows, includeDeleted, onToggleDeleted }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <StatRow><Stat label="Documents" value={rows.length} /></StatRow>
        <label style={{ fontSize: 12.5, color: "#4b5563", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", marginBottom: 12 }}
          title="Documents are soft-deleted rather than removed. Showing them makes a withdrawal visible instead of leaving a silent gap in the record.">
          <input type="checkbox" checked={includeDeleted} onChange={e => onToggleDeleted(e.target.checked)} />
          Show withdrawn
        </label>
      </div>
      <div style={card}>
        {rows.length === 0 ? <Empty>No documents held for this staff member.</Empty> : (
          <Scroll>
            <Table head={["Document", "Incident date", "Notes", ""]}>
              {rows.map(d => (
                <tr key={d._id} style={{ background: d.isDeleted ? "#fef2f2" : "transparent" }}>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {d.documentName}
                    {d.extension && <span style={{ ...P.grey, marginLeft: 6 }}>{d.extension}</span>}
                    {d.isDeleted && <span style={{ ...P.red, marginLeft: 6 }}>withdrawn {fmtDMY(d.deletedAt)}</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(d.incidentDate)}</td>
                  <td style={{ ...td, color: "#4b5563", maxWidth: 340 }}>{d.notes || "—"}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {d.documentUrl && <a href={d.documentUrl} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 12, fontWeight: 600, color: TEAL.fg }}>Open ↗</a>}
                  </td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

function SafetyTab({ nearMisses, nmSummary, incidents, incSummary }) {
  return (
    <>
      <StatRow>
        <Stat label="Near misses" value={nmSummary.total} />
        <Stat label="As dispenser" value={nmSummary.asDispenser} tone={nmSummary.asDispenser ? "#991b1b" : undefined} />
        <Stat label="Caught as checker" value={nmSummary.asChecker} tone={nmSummary.asChecker ? "#166534" : undefined} />
        <Stat label="High-risk LASA" value={nmSummary.highRiskLasa} tone={nmSummary.highRiskLasa ? "#92400e" : undefined} />
        <Stat label="Patient incidents" value={incSummary.total} tone={incSummary.total ? "#991b1b" : undefined} />
      </StatRow>

      {/* The two roles mean opposite things and must not be read as one
          number: dispenser = they made the error, checker = they stopped
          one reaching a patient. */}
      <Note tone={P.blue}>
        <b>Dispenser</b> means the error was theirs. <b>Checker</b> means they caught someone
        else's before it reached a patient — a good catch, not a mistake. Judge the two
        separately, and against the branch cohort in the appraisal evidence pack rather than
        in isolation: raw counts track how much someone dispensed as much as how careful they were.
      </Note>

      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ padding: "9px 14px", borderBottom: "1px solid #f1f5f9", background: "#f9fafb", fontWeight: 700, fontSize: 13.5 }}>
          Near misses
        </div>
        {nearMisses.length === 0 ? <Empty>No near misses attributed to this staff member in the window.</Empty> : (
          <Scroll>
            <Table head={["Date", "Role", "Error type", "Drug", "Cause", ""]}>
              {nearMisses.map(n => (
                <tr key={n._id}>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(n.date)}</td>
                  <td style={td}><span style={n.role === "checker" ? P.green : P.amber}>{n.role}</span></td>
                  <td style={{ ...td, fontWeight: 600 }}>{n.errorType || "—"}</td>
                  <td style={td}>
                    {n.drugName || "—"}
                    {n.wrongDrugName && <span style={{ color: "#991b1b" }}> → {n.wrongDrugName}</span>}
                  </td>
                  <td style={{ ...td, color: "#4b5563" }}>{n.errorCause || "—"}</td>
                  <td style={td}>{n.highRiskLasa && <span style={P.red}>LASA</span>}</td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>

      <div style={card}>
        <div style={{ padding: "9px 14px", borderBottom: "1px solid #f1f5f9", background: "#f9fafb", fontWeight: 700, fontSize: 13.5 }}>
          Patient medication incidents
          <span style={{ fontWeight: 500, color: "#6b7280", marginLeft: 8, fontSize: 12 }}>
            attribution and harm level only — no patient details are shown on this page
          </span>
        </div>
        {incidents.length === 0 ? <Empty>No patient medication incidents attributed in the window.</Empty> : (
          <Scroll>
            <Table head={["Date", "Role", "Harm level"]}>
              {incidents.map(i => (
                <tr key={i._id}>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(i.date)}</td>
                  <td style={td}><span style={i.role === "checker" ? P.green : P.amber}>{i.role}</span></td>
                  <td style={{ ...td, fontWeight: 600 }}>{i.harmLevel || "—"}</td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

/* Cell tinting for a clocking measured against agreed hours.
     arrival — early/on time green · 1–5 min late amber · >5 min red
     leaving — early red · on time to <10 min over green · >=10 min amber
   Unscheduled days get no tint at all: an untinted cell means "no agreed
   hours for this day", which is a different statement from "on time". */
const TONE = {
  green: { bg: "#dcfce7", fg: "#14532d" },
  amber: { bg: "#fef3c7", fg: "#92400e" },
  red:   { bg: "#fee2e2", fg: "#991b1b" }
};

const varianceWords = (v) => {
  if (v == null) return "—";
  if (v === 0) return "exactly on time";
  return v > 0 ? `${v} min late` : `${Math.abs(v)} min early`;
};

/* Clock in / clock out cell: tinted by tone, with a hover panel giving the
   planned and actual times side by side. A native title= tooltip was the
   cheaper option but can't line the two times up, which is the whole
   point — a manager needs to see 08:52 against 09:00, not read a sentence. */
function TimeCell({ actual, planned, tone, varianceMins, kind, openShift }) {
  const [hover, setHover] = useState(false);
  const t = tone ? TONE[tone] : null;

  if (openShift) {
    return <td style={{ ...td, whiteSpace: "nowrap" }}><span style={P.amber}>still open</span></td>;
  }

  return (
    <td style={{ ...td, whiteSpace: "nowrap", position: "relative",
                 background: t ? t.bg : "transparent", color: t ? t.fg : "inherit",
                 fontWeight: t ? 700 : 400, cursor: planned ? "help" : "default" }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}>
      {fmtTime(actual) || "—"}
      {hover && (
        <div style={{
          position: "absolute", zIndex: 30, top: "100%", left: 6, marginTop: 2,
          background: "#111827", color: "#fff", borderRadius: 6, padding: "8px 10px",
          fontSize: 12, fontWeight: 500, lineHeight: 1.5, whiteSpace: "nowrap",
          boxShadow: "0 6px 18px rgba(0,0,0,0.25)"
        }}>
          <div style={{ fontWeight: 800, marginBottom: 3 }}>
            {kind === "in" ? "Clock in" : "Clock out"}
          </div>
          <div>Planned {kind === "in" ? "start" : "end"}: <b>{planned ? fmtTime(planned) : "not scheduled"}</b></div>
          <div>Actual {kind === "in" ? "start" : "end"}: <b>{fmtTime(actual) || "—"}</b></div>
          {planned && (
            <div style={{ marginTop: 3, color: tone === "green" ? "#86efac" : tone === "amber" ? "#fcd34d" : "#fca5a5" }}>
              {varianceWords(varianceMins)}
            </div>
          )}
          {!planned && (
            <div style={{ marginTop: 3, color: "#d1d5db" }}>
              No agreed hours cover this day — nothing to compare against.
            </div>
          )}
        </div>
      )}
    </td>
  );
}

function AttendanceTab({ rows, summary }) {
  const [varianceOnly, setVarianceOnly] = useState(false);

  // "Variance" = anything not green at both ends. Unscheduled shifts are
  // kept in: a shift nobody rostered is exactly the kind of thing this
  // filter exists to surface, even though there is no variance to measure.
  const shown = useMemo(() => rows.filter(a => {
    if (!varianceOnly) return true;
    if (a.openShift) return true;
    if (!a.scheduled) return true;
    return !(a.inTone === "green" && a.outTone === "green");
  }), [rows, varianceOnly]);

  const hidden = rows.length - shown.length;

  return (
    <>
      <StatRow>
        <Stat label="Shifts" value={summary.shifts} />
        <Stat label="Total hours" value={summary.totalHours ?? "—"} />
        <Stat label="Avg shift" value={summary.avgShiftHours != null ? `${summary.avgShiftHours}h` : "—"} />
        <Stat label="Late arrivals" value={summary.lateArrivals}
          tone={summary.lateBeyondGrace ? "#991b1b" : summary.lateArrivals ? "#92400e" : undefined} />
        <Stat label="Avg lateness" value={summary.avgLatenessMins != null ? `${summary.avgLatenessMins}m` : "—"} />
        <Stat label="Left early" value={summary.earlyLeaves} tone={summary.earlyLeaves ? "#991b1b" : undefined} />
        <Stat label="Overtime days" value={summary.overtimeDays} />
        <Stat label="Open shifts" value={summary.openShifts} tone={summary.openShifts ? "#92400e" : undefined} />
      </StatRow>

      {!summary.hasAgreedHours ? (
        <Note tone={P.amber}>
          No agreed hours are recorded for this person in the four-week schedule, so
          nothing can be scored as early or late. Set their pattern in the clock in/out
          panel ("Configure scheduled hours") and these columns will colour automatically.
        </Note>
      ) : (
        <Note>
          Measured against the agreed four-week pattern. <b>Arrival</b>: green = on time or
          early · amber = up to 5 minutes late · red = more than 5. <b>Leaving</b>: red =
          left early · green = on time to under 10 minutes over · amber = 10 minutes or more
          over. Untinted means no agreed hours cover that day — {summary.unscheduledShifts} of{" "}
          {summary.shifts} shifts here. Hover a time to see planned against actual.
        </Note>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <button onClick={() => setVarianceOnly(v => !v)}
          style={{ border: `1px solid ${varianceOnly ? "#0f766e" : "#d1d5db"}`,
                   background: varianceOnly ? "#0f766e" : "#fff",
                   color: varianceOnly ? "#fff" : "#374151",
                   borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 700,
                   cursor: "pointer", fontFamily: "inherit" }}
          title="Hides shifts that were on time at both ends, leaving only late arrivals, early or late finishes, unscheduled shifts and open shifts.">
          {varianceOnly ? "✓ Showing variance entries only" : "Show variance entries only"}
        </button>
        {varianceOnly && (
          <span style={{ fontSize: 12.5, color: "#6b7280" }}>
            {hidden} on-time shift{hidden === 1 ? "" : "s"} hidden
          </span>
        )}
      </div>

      <div style={card}>
        {rows.length === 0 ? <Empty>No clocking history for this staff member in the window.</Empty>
          : shown.length === 0 ? <Empty>Every shift in this window was on time at both ends.</Empty> : (
          <Scroll>
            <Table head={["Date", "In", "Out", "Break", "Net worked", "Notes"]}>
              {shown.map(a => (
                <tr key={a._id}>
                  <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>
                    {fmtDMY(a.clockIn)}
                    {a.overtimeApproved && <span style={{ ...P.purple, marginLeft: 6 }}>OT</span>}
                    {!a.scheduled && <span style={{ ...P.grey, marginLeft: 6 }}>unscheduled</span>}
                  </td>
                  <TimeCell actual={a.clockIn} planned={a.plannedStart} tone={a.inTone}
                    varianceMins={a.inVarianceMins} kind="in" />
                  <TimeCell actual={a.clockOut} planned={a.plannedEnd} tone={a.outTone}
                    varianceMins={a.outVarianceMins} kind="out" openShift={a.openShift} />
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {a.breakCount ? `${fmtMins(a.breakMins)} (${a.breakCount})` : "—"}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 700 }}>{fmtMins(a.netMins)}</td>
                  <td style={{ ...td, color: "#4b5563", maxWidth: 300 }}>{a.notes || "—"}</td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

function SopsTab({ rows, summary }) {
  return (
    <>
      <StatRow>
        <Stat label="Assigned" value={summary.total} />
        <Stat label="Read" value={summary.signed} tone={summary.signed ? "#166534" : undefined} />
        <Stat label="Unread" value={summary.pending} tone={summary.pending ? "#92400e" : undefined} />
        <Stat label="Overdue" value={summary.overdue} tone={summary.overdue ? "#991b1b" : undefined} />
      </StatRow>
      <div style={card}>
        {rows.length === 0 ? <Empty>No SOPs assigned to this staff member.</Empty> : (
          <Scroll>
            <Table head={["SOP", "Due", "Read & signed", "Status"]}>
              {rows.map(r => (
                <tr key={r._id}>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {r.sopTitle}
                    {r.version && <span style={{ color: "#9ca3af", fontWeight: 500 }}> · v{r.version}</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(r.dueDate)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{r.signedAt ? fmtDMY(r.signedAt) : "—"}</td>
                  <td style={td}>
                    <span style={r.status === "signed" ? P.green : r.status === "overdue" ? P.red : P.amber}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

function TrainingTab({ rows, summary }) {
  if (!summary.inUse) {
    return (
      <div style={card}>
        <Empty>
          The training register isn't live yet — no <code>staff_training_assignments</code> records
          exist for this branch. This panel fills in automatically once training cards start being
          issued; nothing further is needed here.
        </Empty>
      </div>
    );
  }
  return (
    <>
      <StatRow>
        <Stat label="Assigned" value={summary.total} />
        <Stat label="Complete" value={summary.complete} tone={summary.complete ? "#166534" : undefined} />
        <Stat label="Pending" value={summary.pending} tone={summary.pending ? "#92400e" : undefined} />
        <Stat label="Overdue" value={summary.overdue} tone={summary.overdue ? "#991b1b" : undefined} />
      </StatRow>
      <div style={card}>
        <Scroll>
          <Table head={["Training", "Requirement", "Due", "Completed", "Status"]}>
            {rows.map(t => (
              <tr key={t._id}>
                <td style={{ ...td, fontWeight: 600 }}>{t.title}</td>
                <td style={td}>{t.requirementLevel ? <span style={P.blue}>{pretty(t.requirementLevel)}</span> : "—"}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(t.dueDate)}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}>{t.completedAt ? fmtDMY(t.completedAt) : "—"}</td>
                <td style={td}>
                  <span style={t.status === "complete" ? P.green : t.status === "overdue" ? P.red : P.amber}>{t.status}</span>
                </td>
              </tr>
            ))}
          </Table>
        </Scroll>
      </div>
    </>
  );
}

function LeaveTab({ rows, summary }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <StatRow>
        <Stat label="Requests" value={summary.total} />
        <Stat label="Approved" value={summary.approved} tone={summary.approved ? "#166534" : undefined} />
        <Stat label="Pending" value={summary.pending} tone={summary.pending ? "#92400e" : undefined} />
        <Stat label="Rejected" value={summary.rejected} />
      </StatRow>
      <div style={card}>
        {rows.length === 0 ? <Empty>No leave requests from this staff member.</Empty> : (
          <Scroll>
            <Table head={["From", "To", "Days", "Status", "Actioned by", "Reason"]}>
              {rows.map(l => (
                <React.Fragment key={l._id}>
                  <tr onClick={() => setOpen(open === l._id ? null : l._id)}
                    style={{ cursor: l.dayCount ? "pointer" : "default" }}
                    title={l.dayCount ? "Click for the per-day breakdown" : undefined}>
                    <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{fmtDMY(l.startDate)}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtDMY(l.endDate)}</td>
                    <td style={td}>{l.dayCount || "—"}</td>
                    <td style={td}>
                      <span style={l.status === "APPROVED" ? P.green : l.status === "PENDING" ? P.amber : P.red}>{pretty(l.status)}</span>
                    </td>
                    <td style={td}>{l.actionedByName || "—"}</td>
                    <td style={{ ...td, color: "#4b5563" }}>{l.reason || "—"}</td>
                  </tr>
                  {open === l._id && l.days.length > 0 && (
                    <tr>
                      <td style={{ ...td, background: "#f9fafb" }} colSpan={6}>
                        {/* Part-days matter: a CUSTOM_HOURS slot is not a day
                            off, so the breakdown shows the actual times. */}
                        {l.days.map((d, i) => (
                          <div key={i} style={{ fontSize: 12.5, color: "#374151", padding: "2px 0" }}>
                            {fmtDMY(d.date)} — {pretty(d.leaveType)}
                            {d.startTime && d.endTime ? ` (${fmtTime(d.startTime)}–${fmtTime(d.endTime)})` : ""}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

function RpTab({ rows, summary }) {
  return (
    <>
      <StatRow>
        <Stat label="Sessions" value={summary.total} />
        <Stat label="Still open" value={summary.openSessions} tone={summary.openSessions ? "#92400e" : undefined} />
        <Stat label="CD keys not returned" value={summary.cdKeysNotReturned} tone={summary.cdKeysNotReturned ? "#991b1b" : undefined} />
      </StatRow>
      <div style={card}>
        {rows.length === 0 ? <Empty>No responsible-pharmacist sessions for this staff member in the window.</Empty> : (
          <Scroll>
            <Table head={["Signed in", "Signed out", "Duration", "CD keys"]}>
              {rows.map(r => (
                <tr key={r._id} style={{ background: r.isSignedOut ? "transparent" : "#fffbeb" }}>
                  <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>{fmtDateTime(r.signInTime)}</td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>
                    {r.isSignedOut ? fmtDateTime(r.signOutTime) : <span style={P.amber}>still signed in</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{fmtMins(r.durationMins)}</td>
                  <td style={td}>
                    {!r.isSignedOut ? "—"
                      : r.cdKeysReturnedToSafe ? <span style={P.green}>returned</span>
                      : <span style={P.red}>not recorded</span>}
                  </td>
                </tr>
              ))}
            </Table>
          </Scroll>
        )}
      </div>
    </>
  );
}

/* Send a message to this one person.
   Posts to /api/headoffice/staff-messages/send — the same endpoint the
   Communications screen uses — so the message lands in the same history
   with the same audit trail, rather than creating a second channel that
   nobody thinks to check.

   compose → confirm → done, mirroring Communications. The confirm step
   survives even though there is only one recipient: the value of "nothing
   has been sent yet" is in re-reading what you wrote, not in checking the
   address list. Email only, because push is not selectable there either. */
function SendMessageDialog({ staff, dbName, onClose, onSent }) {
  const [step, setStep] = useState("compose");   // compose | confirm | sending | done
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const hasEmail = !!staff.email;
  const canProceed = hasEmail && subject.trim() && body.trim();

  const doSend = async () => {
    setStep("sending"); setError(null);
    try {
      const d = await post("/api/headoffice/staff-messages/send", {
        recipients: [{ dbName, staffId: staff.staffId }],
        subject: subject.trim(),
        body: body.trim(),
        method: "email"
      });
      setResult(d); setStep("done");
      if (onSent) onSent();
    } catch (e) { setError(e.message); setStep("confirm"); }
  };

  const field = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13.5, fontFamily: "inherit"
  };
  const btn = (primary, disabled) => ({
    border: `1px solid ${disabled ? "#d1d5db" : primary ? "#0f766e" : "#9ca3af"}`,
    background: disabled ? "#f3f4f6" : primary ? "#0f766e" : "#fff",
    color: disabled ? "#9ca3af" : primary ? "#fff" : "#374151",
    borderRadius: 6, padding: "8px 16px", fontSize: 13.5, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit"
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200,
                  display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={(e) => { if (e.target === e.currentTarget && step !== "sending") onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 10, width: "min(680px, 94vw)",
                    maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "13px 18px", borderBottom: "1px solid #e5e7eb",
                      display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>
              {step === "confirm" ? "Check before sending"
                : step === "done" ? "Message sent"
                : "Send a message"}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {step === "compose" && "Goes to this person only, by email."}
              {step === "confirm" && "Read this back. Nothing has been sent yet."}
              {step === "sending" && "Sending — please wait."}
              {step === "done" && "A record has been added to their message history."}
            </div>
          </div>
          <button onClick={onClose} disabled={step === "sending"}
            style={{ border: "none", background: "none", fontSize: 22, cursor: "pointer",
                     color: "#6b7280", lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: 18, overflowY: "auto", flex: 1, minHeight: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>To</div>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2, marginBottom: 14 }}>
            {staff.name}
            {staff.roleName && <span style={{ fontWeight: 500, color: "#6b7280" }}> · {staff.roleName}</span>}
            <div style={{ fontSize: 12.5, fontWeight: 500, color: hasEmail ? "#4b5563" : "#991b1b", marginTop: 2 }}>
              {hasEmail ? staff.email : "No email address on this staff record — nothing can be sent until one is added."}
            </div>
          </div>

          {step === "compose" && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Subject</div>
              <input style={field} value={subject} maxLength={200} disabled={!hasEmail}
                onChange={e => setSubject(e.target.value)} placeholder="Subject line" />
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3, margin: "14px 0 4px" }}>Message</div>
              <textarea style={{ ...field, minHeight: 200, resize: "vertical", lineHeight: 1.5 }}
                value={body} disabled={!hasEmail}
                onChange={e => setBody(e.target.value)}
                placeholder="Write the message. It is sent as plain text and stored verbatim on their record." />
            </>
          )}

          {step === "confirm" && (
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, background: "#f9fafb" }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{subject.trim()}</div>
              <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.55, marginTop: 8 }}>
                {body.trim()}
              </div>
            </div>
          )}

          {step === "sending" && <div style={{ fontSize: 13.5, color: "#6b7280" }}>Sending…</div>}

          {step === "done" && result && (
            <div style={{ border: `1px solid ${result.failed ? "#fde68a" : "#86efac"}`,
                          background: result.failed ? "#fffbeb" : "#f0fdf4",
                          borderRadius: 8, padding: 14, fontSize: 13.5, fontWeight: 700,
                          color: result.failed ? "#92400e" : "#166534" }}>
              {result.sent} sent
              {result.failed ? ` · ${result.failed} failed` : ""}
              {result.noAddress ? ` · ${result.noAddress} had no address` : ""}
              {(result.results || []).filter(r => r.status !== "sent").map((r, i) => (
                <div key={i} style={{ fontWeight: 500, marginTop: 6 }}>{r.error || r.status}</div>
              ))}
            </div>
          )}

          {error && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 12 }}>Error: {error}</div>}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb",
                      display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          {step === "compose" && (
            <>
              <span style={{ fontSize: 12, color: "#6b7280", marginRight: "auto" }}>
                Sent by email and stored on their record — treat it as part of the file.
              </span>
              <button style={btn(false)} onClick={onClose}>Cancel</button>
              <button style={btn(true, !canProceed)} disabled={!canProceed}
                onClick={() => setStep("confirm")}>Continue →</button>
            </>
          )}
          {step === "confirm" && (
            <>
              <button style={btn(false)} onClick={() => setStep("compose")}>← Back to edit</button>
              <button style={btn(true)} onClick={doSend}>Send now</button>
            </>
          )}
          {step === "done" && <button style={btn(true)} onClick={onClose}>Close</button>}
        </div>
      </div>
    </div>
  );
}

// Messages come from the Communications module's own endpoint, lazily on
// first view — it paginates and can be long, so there is no reason to pull
// it as part of the profile round trip.
function MessagesTab({ dbName, staff }) {
  const staffId = staff.staffId;
  const [state, setState] = useState("loading");
  const [msgs, setMsgs] = useState([]);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [composing, setComposing] = useState(false);
  // Bumped after a successful send so the history refetches and the new
  // message appears without the user having to leave the tab and return.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState("loading"); setErr(null);
      try {
        const d = await post("/api/headoffice/staff-messages/list", { dbName, staffId, limit: 50 });
        if (!cancelled) { setMsgs(d.messages || []); setState("ready"); }
      } catch (e) {
        if (!cancelled) { setErr(e.message); setState("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, [dbName, staffId, reloadKey]);

  const composeBtn = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <span style={{ fontSize: 12.5, color: "#6b7280" }}>
        {staff.email
          ? `Emails go to ${staff.email} and are stored on this record.`
          : "No email address on this staff record — messages can't be sent until one is added."}
      </span>
      <button onClick={() => setComposing(true)} disabled={!staff.email}
        style={{ border: `1px solid ${staff.email ? "#0f766e" : "#d1d5db"}`,
                 background: staff.email ? "#0f766e" : "#f3f4f6",
                 color: staff.email ? "#fff" : "#9ca3af",
                 borderRadius: 6, padding: "7px 15px", fontSize: 13.5, fontWeight: 700,
                 cursor: staff.email ? "pointer" : "not-allowed", fontFamily: "inherit" }}
        title={staff.email
          ? "Opens the same compose dialogue the Communications screen uses — the message lands in this history with the same audit trail."
          : "This staff record has no email address."}>
        ✉ Send message
      </button>
    </div>
  );

  const dialog = composing && (
    <SendMessageDialog
      staff={staff} dbName={dbName}
      onClose={() => setComposing(false)}
      onSent={() => setReloadKey(k => k + 1)} />
  );

  if (state === "loading") return <>{composeBtn}<div style={card}><Empty>Loading messages…</Empty></div>{dialog}</>;
  if (state === "error") return <>{composeBtn}<div style={card}><Empty>Couldn't load messages: {err}</Empty></div>{dialog}</>;
  if (!msgs.length) return <>{composeBtn}<div style={card}><Empty>No messages have been sent to this staff member.</Empty></div>{dialog}</>;

  return (
    <>
    {composeBtn}
    <div style={card}>
      <Scroll>
        {msgs.map((m, idx) => {
          const key = m._id || `${m.sentAt || ""}-${idx}`;
          const long = (m.body || "").length > 320;
          const open = !!expanded[key];
          return (
            <div key={key} style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{m.subject || "(no subject)"}</div>
                <span style={m.status === "failed" ? P.red : m.status === "sent" ? P.green : P.grey}>{m.status || "—"}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>
                {fmtDateTime(m.sentAt)}{m.sourceLabel ? ` · ${m.sourceLabel}` : ""}
              </div>
              {m.body && (
                <div style={{ fontSize: 12.5, color: "#374151", marginTop: 6, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {long && !open ? `${m.body.slice(0, 320)}…` : m.body}
                  {long && (
                    <button onClick={() => setExpanded(x => ({ ...x, [key]: !open }))}
                      style={{ marginLeft: 6, border: "none", background: "none", color: TEAL.fg,
                               fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      {open ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              )}
              {m.status === "failed" && m.error && (
                <div style={{ fontSize: 12, color: "#991b1b", marginTop: 4 }}>{m.error}</div>
              )}
            </div>
          );
        })}
      </Scroll>
    </div>
    {dialog}
    </>
  );
}

/* ---------------- Main ---------------- */

export default function SingleStaff() {
  const masked = useMaskNames();

  const [pharmacies, setPharmacies] = useState([]);
  const [pharmacyName, setPharmacyName] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [staffId, setStaffId] = useState("");
  const [months, setMonths] = useState(12);

  const [profile, setProfile] = useState(null);
  const [includeDeletedDocs, setIncludeDeletedDocs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("documents");
  const [appraisal, setAppraisal] = useState(null);

  // Pharmacy list — the same JWT-scoped fleet endpoint SinglePharmacy.js
  // and Staff.js use. (/api/pharmacies is a typeahead SEARCH endpoint and
  // returns [] without ?q= — do not use it for the full list.)
  useEffect(() => {
    (async () => {
      try {
        const org = localStorage.getItem("organisationName") || "";
        const qs = org ? `?organisationName=${encodeURIComponent(org)}` : "";
        const res = await fetch(`${API}/api/headoffice/single/pharmacies${qs}`, { headers: authHeaders() });
        const data = await res.json();
        setPharmacies((Array.isArray(data) ? data : [])
          .filter(p => p && (p.lookupName || p.pharmacyName))
          .sort((a, b) => (a.pharmacyName || "").localeCompare(b.pharmacyName || "")));
      } catch (e) { console.error("Error fetching pharmacies:", e); }
    })();
  }, []);

  // Changing the inactive toggle can drop the selected person out of the
  // list, so the selection is cleared when they're no longer present
  // rather than left dangling against a stale id.
  const loadStaff = useCallback(async () => {
    if (!pharmacyName) { setStaffList([]); setStaffId(""); return; }
    setError(null);
    try {
      const d = await post("/api/single-staff/list", { pharmacyName, includeInactive: showInactive });
      const list = d.staff || [];
      setStaffList(list);
      setInactiveCount(d.inactiveCount || 0);
      setStaffId(prev => (list.some(s => s.staffId === prev) ? prev : ""));
    } catch (e) { setError(e.message); setStaffList([]); }
  }, [pharmacyName, showInactive]);
  useEffect(() => { loadStaff(); }, [loadStaff]);

  const loadProfile = useCallback(async () => {
    if (!pharmacyName || !staffId) { setProfile(null); return; }
    setLoading(true); setError(null);
    try {
      setProfile(await post("/api/single-staff/profile", { pharmacyName, staffId, includeDeletedDocs, months }));
    } catch (e) { setError(e.message); setProfile(null); }
    finally { setLoading(false); }
  }, [pharmacyName, staffId, includeDeletedDocs, months]);
  useEffect(() => { loadProfile(); }, [loadProfile]);

  const summary = profile?.appraisalSummary;

  // RP sessions only exist for pharmacists — the tab is hidden entirely
  // for everyone else rather than shown permanently empty.
  const tabs = useMemo(() => {
    if (!profile) return [];
    const isPharmacist = /pharmacist/i.test(profile.staff.roleName || "");
    const t = [
      { key: "documents",  label: "Documents",  count: profile.documents.length },
      { key: "safety",     label: "Safety",     count: profile.nearMissSummary.total + profile.incidentSummary.total },
      { key: "attendance", label: "Attendance", count: profile.attendanceSummary.shifts },
      { key: "sops",       label: "SOPs",       count: profile.sopSummary.total },
      { key: "training",   label: "Training",   count: profile.trainingSummary.inUse ? profile.trainingSummary.total : null },
      { key: "leave",      label: "Leave",      count: profile.leaveSummary.total }
    ];
    if (isPharmacist || profile.rpSummary.total > 0) {
      t.push({ key: "rp", label: "RP sessions", count: profile.rpSummary.total });
    }
    t.push({ key: "messages", label: "Messages", count: null });
    return t;
  }, [profile]);

  // Keep the active tab valid when the person changes — a pharmacist's RP
  // tab disappearing under a dispenser would otherwise blank the page.
  useEffect(() => {
    if (tabs.length && !tabs.some(t => t.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: 20 }}>Single staff</h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
            Everything held about one person — appraisals, safety record, attendance,
            SOPs, training, leave, documents and messages. Leavers stay searchable
            via "Show inactive".
          </div>
        </div>
        <MaskToggleButton />
      </div>

      {/* Selectors */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          PHARMACY
          <select value={pharmacyName} onChange={e => { setPharmacyName(e.target.value); setStaffId(""); }} style={selStyle}>
            <option value="">Select a pharmacy…</option>
            {pharmacies.map(p => {
              const v = p.lookupName || p.pharmacyName;
              return <option key={v} value={v}>{displayName(p.pharmacyName || v, masked)}</option>;
            })}
          </select>
        </label>

        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          STAFF MEMBER
          <select value={staffId} onChange={e => setStaffId(e.target.value)} style={selStyle} disabled={!pharmacyName}>
            <option value="">{pharmacyName ? "Select a staff member…" : "Pick a pharmacy first"}</option>
            {staffList.map(s => (
              <option key={s.staffId} value={s.staffId}>
                {displayName(s.name, masked)}{s.roleName ? ` — ${s.roleName}` : ""}{s.active ? "" : "  (inactive)"}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: "inline-flex", flexDirection: "column", gap: 4, fontSize: 11, color: "#6b7280" }}>
          PERIOD
          <select value={months} onChange={e => setMonths(Number(e.target.value))} style={{ ...selStyle, minWidth: 150 }}
            title="Window for the safety, attendance and RP panels. Documents, SOPs and leave always show the full history.">
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
            <option value={36}>Last 36 months</option>
          </select>
        </label>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", paddingBottom: 8, cursor: "pointer" }}
          title="Includes staff marked inactive — leavers keep their documents, SOP history and leave record.">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive staff{inactiveCount > 0 && showInactive ? ` (${inactiveCount})` : ""}
        </label>
      </div>

      {error && <div style={{ color: "#dc2626", fontSize: 13, marginBottom: 12 }}>Error: {error}</div>}

      {!staffId ? (
        <div style={{ ...card, padding: 28, textAlign: "center", color: "#6b7280", fontSize: 14 }}>
          Select a pharmacy and a staff member to open their file.
        </div>
      ) : loading ? (
        <div style={{ ...card, padding: 28, textAlign: "center", color: "#6b7280", fontSize: 14 }}>Loading…</div>
      ) : profile ? (
        <>
          {/* Header card */}
          <div style={{ ...card, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
                  {displayName(profile.staff.name, masked)}
                  {!profile.staff.active && <span style={{ ...P.grey, marginLeft: 8, verticalAlign: "middle" }}>inactive</span>}
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                  {profile.staff.roleName || "Role not set"} · {displayName(pharmacyName, masked)}
                  {profile.staff.email ? ` · ${profile.staff.email}` : ""}
                  {` · record started ${fmtDMY(profile.staff.createdAt)}`}
                </div>
              </div>
              <button
                onClick={() => setAppraisal({
                  staffId: profile.staff.staffId,
                  staffName: profile.staff.name,
                  roleName: profile.staff.roleName
                })}
                style={{ border: "1px solid #0f766e", background: "#0f766e", color: "#fff", borderRadius: 6,
                         padding: "9px 16px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                title="Opens the full appraisal record — a tab per full appraisal and a tab per simple record.">
                Open appraisal record →
              </button>
            </div>

            {/* Appraisal dates from BOTH collections; teal marks the simple tool. */}
            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>Last full appraisal</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{fmtDMY(summary.lastAdvancedDate)}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>Last simple record</div>
                <div style={{ marginTop: 2 }}>
                  {summary.lastSimpleDate
                    ? <span style={{ ...P.teal, fontSize: 13 }}>{fmtDMY(summary.lastSimpleDate)}</span>
                    : <span style={{ fontSize: 14, fontWeight: 700 }}>—</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>Next review due</div>
                <div style={{ marginTop: 2 }}>
                  {summary.nextSimpleDue
                    ? <span style={{ ...P.teal, fontSize: 13 }}>{fmtDMY(summary.nextSimpleDue)}</span>
                    : <span style={{ fontSize: 14, fontWeight: 700 }}>—</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.3 }}>Records held</div>
                <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                  {summary.advancedCount} full · <span style={{ color: TEAL.fg }}>{summary.simpleCount} simple</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", marginBottom: 14 }}>
            {tabs.map(t => {
              const on = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  style={{ border: "none", borderBottom: on ? "3px solid #0f766e" : "3px solid transparent",
                           background: "none", padding: "7px 14px", fontSize: 13.5, fontWeight: on ? 800 : 600,
                           color: on ? "#0f766e" : "#334155", cursor: "pointer", fontFamily: "inherit",
                           appearance: "none", WebkitAppearance: "none" }}>
                  {t.label}
                  {t.count != null && (
                    <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 700, color: on ? "#0f766e" : "#9ca3af" }}>
                      ({t.count})
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {tab === "documents"  && <DocumentsTab rows={profile.documents} includeDeleted={includeDeletedDocs} onToggleDeleted={setIncludeDeletedDocs} />}
          {tab === "safety"     && <SafetyTab nearMisses={profile.nearMisses} nmSummary={profile.nearMissSummary} incidents={profile.incidents} incSummary={profile.incidentSummary} />}
          {tab === "attendance" && <AttendanceTab rows={profile.attendance} summary={profile.attendanceSummary} />}
          {tab === "sops"       && <SopsTab rows={profile.sops} summary={profile.sopSummary} />}
          {tab === "training"   && <TrainingTab rows={profile.training} summary={profile.trainingSummary} />}
          {tab === "leave"      && <LeaveTab rows={profile.leave} summary={profile.leaveSummary} />}
          {tab === "rp"         && <RpTab rows={profile.rpSessions} summary={profile.rpSummary} />}
          {tab === "messages"   && <MessagesTab dbName={profile.dbName} staff={profile.staff} />}
        </>
      ) : null}

      {appraisal && (
        <StaffAppraisal
          pharmacyName={pharmacyName}
          staffId={appraisal.staffId}
          staffName={appraisal.staffName}
          roleName={appraisal.roleName}
          onClose={() => { setAppraisal(null); loadProfile(); }}
        />
      )}
    </div>
  );
}